/**
 * Paywall A/B — four gorgeous "made with Any AI" treatments, all built from a
 * moving masonry of real generated media. A visitor is bucketed once (sticky in
 * localStorage) so the experience is stable across opens, and the assigned
 * variant rides along on the Polar checkout as `paywall_variant` metadata — that
 * is how we attribute conversions per treatment without an analytics SDK.
 *
 * The treatments (mirroring the approved concept demo):
 *   a — moving IMAGE masonry, immersive (copy over the grid)
 *   b — moving IMAGE masonry, copy on a solid lower band
 *   d — moving VIDEO masonry, split (solid copy panel beside the grid)
 */

export type PaywallVariantId = "a" | "b" | "d";

export interface PaywallVariant {
  id: PaywallVariantId;
  /** Whether the masonry tiles are still images or autoplaying clips. */
  kind: "img" | "vid";
  /** How the copy sits against the grid. */
  layout: "immersive" | "bottom" | "split";
  /** Column count at full width; scaled down on narrow viewports. */
  cols: number;
  /** i18n key under `paywall.v.*` for this treatment's headline. */
  headlineKey: string;
}

export const PAYWALL_VARIANTS: readonly PaywallVariant[] = [
  { id: "a", kind: "img", layout: "immersive", cols: 7, headlineKey: "paywall.v.a" },
  { id: "b", kind: "img", layout: "bottom", cols: 8, headlineKey: "paywall.v.b" },
  { id: "d", kind: "vid", layout: "split", cols: 4, headlineKey: "paywall.v.d" },
];

const CDN = "https://cdn.anyaistudio.com/landing/gen/";

/** Curated stills (webp) — the same keepers used across the marketing site. */
export const PAYWALL_IMAGES: readonly string[] = [
  "tw-02", "dv-03", "dv-04", "dv-06", "dv-13", "dv-12", "dv-14", "tw-01",
  "tw-05", "dv-10", "tw-10", "dv-05", "dv-07", "dv-08", "dv-02",
].map((n) => `${CDN}${n}.webp`);

/** Curated clips (mp4) — each has a matching `-poster.webp` for instant paint. */
export const PAYWALL_VIDEOS: readonly string[] = [
  "vid-serum", "dvid-01", "dvid-02", "dvid-06", "vid-02",
  "vid-05", "dvid-05", "dvid-04", "dvid-03", "vid-04",
].map((n) => `${CDN}${n}.mp4`);

/** The poster still for a clip URL (`…/x.mp4` → `…/x-poster.webp`). */
export function posterFor(videoUrl: string): string {
  return videoUrl.replace(/\.mp4$/, "-poster.webp");
}

const STORAGE_KEY = "aa.paywall-variant";

function isVariantId(value: string | null): value is PaywallVariantId {
  return value === "a" || value === "b" || value === "d";
}

/**
 * The visitor's sticky variant. Assigned once at random (uniform across the
 * four) and persisted, so a returning user always sees the same paywall — the
 * only honest way to read a conversion delta. `seed` (0..1) is injectable so
 * assignment stays deterministic in tests; production omits it.
 */
export function getPaywallVariant(seed?: number): PaywallVariant {
  // QA/preview override: `?pwv=a|b|c|d` forces a treatment. Stashed in
  // sessionStorage so it survives the auth redirect (which drops the query) and
  // page reloads, letting all four be reviewed from one browser.
  try {
    const q = new URLSearchParams(window.location.search).get("pwv");
    if (isVariantId(q)) sessionStorage.setItem("aa.pwv", q);
    const forced = isVariantId(q) ? q : sessionStorage.getItem("aa.pwv");
    if (isVariantId(forced)) {
      const found = PAYWALL_VARIANTS.find((v) => v.id === forced);
      if (found) return found;
    }
  } catch {
    // No window/search/storage — ignore and fall through to normal assignment.
  }

  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode / storage disabled — fall through to an ephemeral pick.
  }
  if (isVariantId(stored)) {
    const found = PAYWALL_VARIANTS.find((v) => v.id === stored);
    if (found) return found;
  }
  const r = seed ?? Math.random();
  const picked = PAYWALL_VARIANTS[Math.min(PAYWALL_VARIANTS.length - 1, Math.floor(r * PAYWALL_VARIANTS.length))];
  try {
    localStorage.setItem(STORAGE_KEY, picked.id);
  } catch {
    // Non-fatal: the pick just won't be sticky this session.
  }
  return picked;
}

let preloaded = false;

/**
 * Warm the assigned variant's media in the background so the paywall paints
 * instantly on first open. Images (and video posters) are cheap webp; the clips
 * themselves stream on open. Safe to call repeatedly — it runs at most once and
 * only when the browser is idle, so it never contends with first render.
 */
export function preloadPaywallMedia(): void {
  if (preloaded || typeof window === "undefined") return;
  preloaded = true;
  const run = (): void => {
    const variant = getPaywallVariant();
    const urls =
      variant.kind === "vid"
        ? PAYWALL_VIDEOS.map(posterFor)
        : PAYWALL_IMAGES;
    for (const url of urls) {
      const img = new Image();
      img.decoding = "async";
      img.src = url;
    }
  };
  const idle = (window as unknown as { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback;
  if (idle) idle(run);
  else window.setTimeout(run, 1200);
}
